// =============================================================================
// FILE PATH : src/modules/pms/wbs_template/components/TemplateGroupList.js
// MODULE    : PMS → WBS Template Integration
// PURPOSE   : Displays grouped templates (Project / Equipment)
// =============================================================================

import React, { useState } from "react";

import TemplateTaskList from "./TemplateTaskList";


// -----------------------------------------------------------------------------
// STYLES
// -----------------------------------------------------------------------------
const groupHeaderStyle = {
  fontWeight: "600",
  marginTop: "12px",
  marginBottom: "6px",
  fontSize: "15px"
};

const equipmentCardStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  marginBottom: "14px",
  overflow: "hidden",
  background: "#fff"
};

const equipmentHeaderStyle = {
  padding: "10px 12px",
  background: "#f8fafc",
  borderBottom: "1px solid #e5e7eb",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  cursor: "pointer"
};

const templateHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "8px 10px",
  cursor: "pointer",
  background: "#fafafa",
  borderRadius: "6px",
  marginTop: "8px"
};

const templateContainerStyle = {
  padding: "10px"
};

const actionButtonStyle = {
  padding: "4px 8px",
  fontSize: "12px",
  border: "1px solid #d1d5db",
  borderRadius: "4px",
  background: "#fff",
  cursor: "pointer"
};


// -----------------------------------------------------------------------------
// COMPONENT
// -----------------------------------------------------------------------------
const TemplateGroupList = ({
  mode,
  templates = [],
  selectedTasks,
  onToggle
}) => {

  // ---------------------------------------------------------------------------
  // EXPAND / COLLAPSE STATE
  // ---------------------------------------------------------------------------
  const [expandedEquipment, setExpandedEquipment] = useState({});
  const [expandedTemplates, setExpandedTemplates] = useState({});

  // ---------------------------------------------------------------------------
  // TOGGLE EQUIPMENT
  // ---------------------------------------------------------------------------
  const toggleEquipment = (equipmentId) => {

    setExpandedEquipment((prev) => ({
      ...prev,
      [equipmentId]: !prev[equipmentId]
    }));
  };

  // ---------------------------------------------------------------------------
  // TOGGLE TEMPLATE
  // ---------------------------------------------------------------------------
  const toggleTemplate = (templateId) => {

    setExpandedTemplates((prev) => ({
      ...prev,
      [templateId]: !prev[templateId]
    }));
  };

  // ---------------------------------------------------------------------------
  // SELECT ALL TASKS
  // ---------------------------------------------------------------------------
    const handleSelectAll = (
    tasks = [],
    templateName,
    templateType
  ) => {

    tasks.forEach((task) => {

      const alreadySelected = selectedTasks.some(
        (t) =>
          t.task_name === task.task_name &&
          t.template_name === templateName
      );

      if (!alreadySelected) {

        onToggle(
          task,
          templateName,
          templateType
        );
      }
    });
  };

  // ---------------------------------------------------------------------------
  // NO DATA
  // ---------------------------------------------------------------------------
  if (!templates.length) {

    return (
      <div style={{
        padding: "12px",
        color: "#666"
      }}>
        No templates found
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // PROJECT MODE
  // ---------------------------------------------------------------------------
  if (mode === "PROJECT") {

    return (
      <div>

        <div style={groupHeaderStyle}>
          Project Templates
        </div>

        {templates.map((tpl) => {

          const isExpanded =
            expandedTemplates[tpl.template_id] ?? true;

          return (
            <div
              key={tpl.template_id}
              style={equipmentCardStyle}
            >

              {/* TEMPLATE HEADER */}
              <div
                style={templateHeaderStyle}
                onClick={() => toggleTemplate(tpl.template_id)}
              >

                <div style={{
                  fontWeight: "600"
                }}>
                  {isExpanded ? "▼" : "▶"} {tpl.template_name}
                </div>

                <button
                  style={actionButtonStyle}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectAll(
                    tpl.tasks,
                    tpl.template_name,
                    "PROJECT"
                  );
                  }}
                >
                  Select All
                </button>

              </div>

              {/* TASKS */}
              {isExpanded && (
                <div style={templateContainerStyle}>

                  <TemplateTaskList
                  tasks={tpl.tasks}
                  selectedTasks={selectedTasks}
                  onToggle={(task) =>
                    onToggle(
                      task,
                      tpl.template_name,
                      "PROJECT"
                    )
                  }
                />

                </div>
              )}

            </div>
          );
        })}

      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // ITEM MODE
  // ---------------------------------------------------------------------------
  if (mode === "ITEM") {

    return (
      <div>

        <div style={groupHeaderStyle}>
          Equipment Templates
        </div>

        {templates.map((eq) => {

          const equipmentExpanded =
            expandedEquipment[eq.equipment_id] ?? true;

          return (
            <div
              key={eq.equipment_id}
              style={equipmentCardStyle}
            >

              {/* EQUIPMENT HEADER */}
              <div
                style={equipmentHeaderStyle}
                onClick={() => toggleEquipment(eq.equipment_id)}
              >

                <div>

                  <div style={{
                    fontWeight: "600",
                    color: "#1976d2"
                  }}>
                    {equipmentExpanded ? "▼" : "▶"} 📦 {eq.equipment_name}
                  </div>

                  {/* CATEGORY */}
                  {eq.equipment_category && (
                    <div style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "2px"
                    }}>
                      Category : {eq.equipment_category}
                    </div>
                  )}

                  {/* PRODUCT VARIANT */}
                  {eq.product_variant_name && (
                    <div style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "2px"
                    }}>
                      Variant : {eq.product_variant_name}
                    </div>
                  )}

                </div>

              </div>

              {/* EQUIPMENT BODY */}
              {equipmentExpanded && (

                <div style={{
                  padding: "10px"
                }}>

                  {eq.templates.map((tpl) => {

                    const templateExpanded =
                      expandedTemplates[tpl.template_id] ?? true;

                    return (
                      <div
                        key={tpl.template_id}
                        style={{
                          marginBottom: "10px"
                        }}
                      >

                        {/* TEMPLATE HEADER */}
                        <div
                          style={templateHeaderStyle}
                          onClick={() => toggleTemplate(tpl.template_id)}
                        >

                          <div style={{
                            fontWeight: "500"
                          }}>
                            {templateExpanded ? "▼" : "▶"} {tpl.template_name}
                          </div>

                          <button
                            style={actionButtonStyle}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectAll(
                                tpl.tasks,
                                tpl.template_name,
                                "EQUIPMENT"
                              );
                            }}
                          >
                            Select All
                          </button>

                        </div>

                        {/* TASKS */}
                        {templateExpanded && (
                          <div style={{
                            marginTop: "6px"
                          }}>

                            <TemplateTaskList
                            tasks={tpl.tasks}
                            selectedTasks={selectedTasks}
                            onToggle={(task) =>
                              onToggle(
                                task,
                                tpl.template_name,
                                "EQUIPMENT"
                              )
                            }
                          />

                          </div>
                        )}

                      </div>
                    );
                  })}

                </div>
              )}

            </div>
          );
        })}

      </div>
    );
  }

  return null;
};

export default TemplateGroupList;